import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  X,
  Loader2,
  ChevronDown,
  Plus,
  Mail,
  Eye,
  Check,
  Send,
  Sparkles,
  AlertTriangle,
  Bell,
  CalendarOff,
  Plane,
  PlaneLanding,
} from "lucide-react";
import { get, post, put, del } from "../api/client";
import { Partner, Tab, UserPublic, KamVacation } from "../types";

// ── Types ────────────────────────────────────────────────────────────────────

interface KAMUser extends UserPublic {
  role: "kam";
}

interface HMPUser extends UserPublic {
  role: "hmp";
}

interface AdminUser extends UserPublic {
  role: "admin";
}

type ActiveTab = "kams" | "hmps" | "admins";

// ── Shared helpers ────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
      <strong>Fout bij laden:</strong> {message}
    </div>
  );
}

function EmptyState({ message, icon: Icon }: { message: string; icon: React.ElementType }) {
  return (
    <div className="py-16 text-center text-slate-400">
      <Icon size={40} className="mx-auto mb-3 opacity-30" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ── Confirm Delete Dialog ─────────────────────────────────────────────────────

interface ConfirmDialogProps {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}

function ConfirmDialog({ name, onConfirm, onCancel, saving }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-slate-800 text-lg">Gebruiker verwijderen?</h3>
        <p className="text-sm text-slate-600">
          Weet je zeker dat je <strong>{name}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
        </p>
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
          <button onClick={onCancel} className="btn-secondary min-h-[44px] sm:min-h-0">
            Annuleren
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Verwijderen
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: "success" | "error";
  onDismiss: () => void;
}

function Toast({ message, type, onDismiss }: ToastProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all duration-300 max-w-sm w-full mx-4 ${
        type === "success"
          ? "bg-green-600 text-white"
          : "bg-red-600 text-white"
      }`}
    >
      {type === "success" ? <Check size={16} /> : <X size={16} />}
      <span className="flex-1">{message}</span>
      <button
        onClick={onDismiss}
        className="p-1 rounded hover:bg-white/20 transition-colors"
        aria-label="Sluiten"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── HMP Status Badge (Uitgenodigd / Geactiveerd) ────────────────────────────

function HMPStatusBadge({ lastLogin }: { lastLogin?: string | null }) {
  if (lastLogin) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Check size={11} />
        Geactiveerd
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <Mail size={11} />
      Uitgenodigd
    </span>
  );
}

// ── Onboarding Confirm Dialog ─────────────────────────────────────────

interface OnboardingConfirmDialogProps {
  hmp: { name: string; email: string; id: number };
  isResend: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

function OnboardingConfirmDialog({
  hmp,
  isResend,
  onConfirm,
  onCancel,
  sending,
}: OnboardingConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-waves-blue-light flex items-center justify-center flex-shrink-0">
            <Sparkles size={20} style={{ color: "#65ADDD" }} />
          </div>
          <h3 className="font-semibold text-slate-800 text-lg">
            {isResend ? "Welkomstmail opnieuw versturen?" : "Welkomstmail versturen?"}
          </h3>
        </div>
        <p className="text-sm text-slate-600">
          Verstuur de onboarding-mail naar <strong>{hmp.name}</strong> ({hmp.email})?
          De magic link is 14 dagen geldig.
        </p>
        <button
          type="button"
          onClick={() => window.open(`/api/admin/mail/onboarding/${hmp.id}/preview`, "_blank")}
          className="flex items-center gap-2 text-sm text-waves-blue hover:underline"
          style={{ color: "#65ADDD" }}
        >
          <Eye size={14} />
          Voorbeeld bekijken
        </button>
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
          <button
            onClick={onCancel}
            disabled={sending}
            className="btn-secondary min-h-[44px] sm:min-h-0"
          >
            Annuleren
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] sm:min-h-0 text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: "#65ADDD" }}
          >
            {sending ? (
              <><Loader2 size={14} className="animate-spin" />Versturen…</>
            ) : (
              <><Send size={14} />Versturen</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mail Confirm Dialog ───────────────────────────────────────────────────────

interface MailConfirmDialogProps {
  hmp: { name: string; email: string; id: number };
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

function MailConfirmDialog({ hmp, onConfirm, onCancel, sending }: MailConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-waves-blue-light flex items-center justify-center flex-shrink-0">
            <Mail size={20} style={{ color: "#65ADDD" }} />
          </div>
          <h3 className="font-semibold text-slate-800 text-lg">Review-mail versturen?</h3>
        </div>
        <p className="text-sm text-slate-600">
          Verstuur review-mail naar <strong>{hmp.name}</strong> ({hmp.email})?
        </p>
        <button
          type="button"
          onClick={() => window.open(`/api/admin/mail/preview/${hmp.id}`, "_blank")}
          className="flex items-center gap-2 text-sm hover:underline"
          style={{ color: "#65ADDD" }}
        >
          <Eye size={14} />
          Voorbeeld bekijken
        </button>
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
          <button onClick={onCancel} disabled={sending} className="btn-secondary min-h-[44px] sm:min-h-0">
            Annuleren
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] sm:min-h-0 text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: "#65ADDD" }}
          >
            {sending ? (
              <><Loader2 size={14} className="animate-spin" />Versturen…</>
            ) : (
              <><Send size={14} />Versturen</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── KAM Modal ────────────────────────────────────────────────────────────────

interface KAMModalProps {
  initial?: KAMUser | null;
  onClose: () => void;
  onSaved: () => void;
}

function KAMModal({ initial, onClose, onSaved }: KAMModalProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!initial;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Naam en e-mail zijn verplicht.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await put(`/users/${initial!.id}`, { name: name.trim(), email: email.trim() });
      } else {
        await post("/users", { name: name.trim(), email: email.trim(), role: "kam" });
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-slate-800 text-lg">
            {isEdit ? "KAM Bewerken" : "KAM Toevoegen"}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Naam <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijv. Jan de Vries"
              className="input-field"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              E-mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jan@waves.nl"
              className="input-field"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary min-h-[44px] sm:min-h-0">
              Annuleren
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Opslaan…
                </>
              ) : isEdit ? (
                "Wijzigingen Opslaan"
              ) : (
                "KAM Aanmaken"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── HMP Modal ────────────────────────────────────────────────────────────────

interface HMPModalProps {
  initial?: HMPUser | null;
  partners: Partner[];
  kams: KAMUser[];
  onClose: () => void;
  onSaved: () => void;
}

function HMPModal({ initial, partners, kams, onClose, onSaved }: HMPModalProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [partnerId, setPartnerId] = useState<number | "">(initial?.partner_id ?? "");
  const [kamId, setKamId] = useState<number | "">(initial?.kam_id ?? "");
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedTabs, setSelectedTabs] = useState<Set<number>>(
    new Set(initial?.tab_permissions ?? [])
  );
  const [reviewIntervalDays, setReviewIntervalDays] = useState<string>(
    initial?.review_interval_days != null ? String(initial.review_interval_days) : ""
  );
  const [inactivityAlertDays, setInactivityAlertDays] = useState<string>(
    initial?.inactivity_alert_days != null ? String(initial.inactivity_alert_days) : ""
  );
  const [noChangesAlertDays, setNoChangesAlertDays] = useState<string>(
    initial?.no_changes_alert_days != null ? String(initial.no_changes_alert_days) : ""
  );
  const [tabsLoading, setTabsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Inline new tab form
  const [showNewTab, setShowNewTab] = useState(false);
  const [newTabName, setNewTabName] = useState("");
  const [newTabLocation, setNewTabLocation] = useState("");
  const [newTabSaving, setNewTabSaving] = useState(false);
  const [newTabError, setNewTabError] = useState("");

  const isEdit = !!initial;

  // Load tabs when partner changes
  useEffect(() => {
    if (!partnerId) {
      setTabs([]);
      setSelectedTabs(new Set());
      return;
    }
    setTabsLoading(true);
    get<Tab[]>(`/tabs?partner_id=${partnerId}`)
      .then((t) => {
        setTabs(t);
        // On edit, keep existing selections that belong to this partner
        if (!isEdit) setSelectedTabs(new Set());
      })
      .catch(() => setTabs([]))
      .finally(() => setTabsLoading(false));
  }, [partnerId]);

  function toggleTab(tabId: number) {
    setSelectedTabs((prev) => {
      const next = new Set(prev);
      if (next.has(tabId)) next.delete(tabId);
      else next.add(tabId);
      return next;
    });
  }

  async function handleAddNewTab() {
    if (!partnerId) return;
    if (!newTabName.trim()) {
      setNewTabError("Naam is verplicht.");
      return;
    }
    setNewTabSaving(true);
    setNewTabError("");
    try {
      const created = await post<Tab>("/tabs", {
        partner_id: partnerId,
        name: newTabName.trim(),
        location: newTabLocation.trim() || newTabName.trim(),
      });
      setTabs((prev) => [...prev, created]);
      setSelectedTabs((prev) => new Set([...prev, created.id]));
      setNewTabName("");
      setNewTabLocation("");
      setShowNewTab(false);
    } catch (err: unknown) {
      setNewTabError(err instanceof Error ? err.message : "Aanmaken mislukt.");
    } finally {
      setNewTabSaving(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Naam en e-mail zijn verplicht.");
      return;
    }
    // Validate review interval if provided
    let parsedReviewInterval: number | null = null;
    if (reviewIntervalDays.trim() !== "") {
      const days = parseInt(reviewIntervalDays, 10);
      if (isNaN(days) || days < 7 || days > 365) {
        setError("Review-termijn moet tussen 7 en 365 dagen liggen.");
        return;
      }
      parsedReviewInterval = days;
    }
    setSaving(true);
    setError("");
    try {
      const parsedInactivity = inactivityAlertDays.trim() !== "" && Number(inactivityAlertDays) > 0
        ? Number(inactivityAlertDays)
        : null;
      const parsedNoChanges = noChangesAlertDays.trim() !== "" && Number(noChangesAlertDays) > 0
        ? Number(noChangesAlertDays)
        : null;

      const payload = {
        name: name.trim(),
        email: email.trim(),
        role: "hmp",
        partner_id: partnerId !== "" ? partnerId : null,
        kam_id: kamId !== "" ? kamId : null,
        tab_permissions: Array.from(selectedTabs),
        review_interval_days: parsedReviewInterval,
        inactivity_alert_days: parsedInactivity,
        no_changes_alert_days: parsedNoChanges,
      };
      if (isEdit) {
        await put(`/users/${initial!.id}`, payload);
      } else {
        await post("/users", payload);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-slate-800 text-lg">
            {isEdit ? "HMP Bewerken" : "HMP Toevoegen"}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Naam */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Naam <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijv. Maria Jansen"
              className="input-field"
              autoFocus
            />
          </div>

          {/* E-mail */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              E-mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="maria@partner.nl"
              className="input-field"
            />
          </div>

          {/* Partner */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Partner
            </label>
            <div className="relative">
              <select
                value={partnerId}
                onChange={(e) =>
                  setPartnerId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="input-field appearance-none pr-10"
              >
                <option value="">— Selecteer partner —</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Vestigingen */}
          {partnerId !== "" && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  Vestigingen
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTab((v) => !v);
                    setNewTabError("");
                  }}
                  className="flex items-center gap-1 text-xs font-medium text-waves-blue hover:text-waves-blue-hover transition-colors min-h-[36px]"
                >
                  <Plus size={12} />
                  Vestiging toevoegen
                </button>
              </div>

              {tabsLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                  <Loader2 size={14} className="animate-spin" />
                  Vestigingen laden…
                </div>
              ) : tabs.length === 0 ? (
                <p className="text-sm text-slate-400 italic">
                  Geen vestigingen gevonden voor deze partner.
                </p>
              ) : (
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {tabs.map((tab) => (
                    <label
                      key={tab.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer min-h-[48px]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTabs.has(tab.id)}
                        onChange={() => toggleTab(tab.id)}
                        className="w-4 h-4 rounded border-slate-300 text-waves-blue focus:ring-waves-blue"
                      />
                      <span className="text-sm text-slate-700 flex-1">{tab.name}</span>
                      {tab.location && tab.location !== tab.name && (
                        <span className="text-xs text-slate-400">{tab.location}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              {/* Inline new tab form */}
              {showNewTab && (
                <div className="mt-3 p-4 border border-waves-blue/30 bg-waves-blue-light/20 rounded-xl space-y-3">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Nieuwe Vestiging
                  </p>
                  {/* Stack on mobile, grid on sm+ */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Naam <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTabName}
                        onChange={(e) => setNewTabName(e.target.value)}
                        placeholder="Vestigingnaam"
                        className="input-field py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Locatie
                      </label>
                      <input
                        type="text"
                        value={newTabLocation}
                        onChange={(e) => setNewTabLocation(e.target.value)}
                        placeholder="Optioneel"
                        className="input-field py-1.5 text-sm"
                      />
                    </div>
                  </div>
                  {newTabError && (
                    <p className="text-xs text-red-600">{newTabError}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAddNewTab}
                      disabled={newTabSaving}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-waves-blue text-white hover:bg-waves-blue-hover transition-colors min-h-[40px]"
                    >
                      {newTabSaving ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Plus size={12} />
                      )}
                      Aanmaken
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewTab(false);
                        setNewTabError("");
                        setNewTabName("");
                        setNewTabLocation("");
                      }}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors min-h-[40px]"
                    >
                      <X size={12} />
                      Annuleren
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Primaire KAM */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Primaire KAM
            </label>
            <div className="relative">
              <select
                value={kamId}
                onChange={(e) =>
                  setKamId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="input-field appearance-none pr-10"
              >
                <option value="">— Geen KAM geselecteerd —</option>
                {kams.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Review-termijn */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Review-termijn
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={7}
                max={365}
                value={reviewIntervalDays}
                onChange={(e) => setReviewIntervalDays(e.target.value)}
                placeholder="30"
                className="input-field w-28"
              />
              <span className="text-sm text-slate-500 flex-shrink-0">dagen</span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Aantal dagen voordat een vacature ter review wordt aangeboden. Standaard: 30 dagen.
            </p>
          </div>

          {/* Mail-instellingen */}
          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Bell size={14} />
              Mail-instellingen
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Inactiviteits-alert (dagen)
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={inactivityAlertDays}
                  onChange={(e) => setInactivityAlertDays(e.target.value)}
                  placeholder="Bijv. 10"
                  className="input-field w-full"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {inactivityAlertDays && Number(inactivityAlertDays) > 0
                    ? `Alert na ${inactivityAlertDays} dagen inactiviteit`
                    : "Uit"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Geen-wijzigingen-alert (dagen)
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={noChangesAlertDays}
                  onChange={(e) => setNoChangesAlertDays(e.target.value)}
                  placeholder="Bijv. 14"
                  className="input-field w-full"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {noChangesAlertDays && Number(noChangesAlertDays) > 0
                    ? `Alert na ${noChangesAlertDays} dagen geen wijzigingen`
                    : "Uit"}
                </p>
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary min-h-[44px] sm:min-h-0">
              Annuleren
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Opslaan…
                </>
              ) : isEdit ? (
                "Wijzigingen Opslaan"
              ) : (
                "HMP Aanmaken"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── KAMs Tab ─────────────────────────────────────────────────────────────────

interface KAMsTabProps {
  kams: KAMUser[];
  hmps: HMPUser[];
  onRefresh: () => void;
}

// ── KAM Vacation status badge ──────────────────────────────────────────────

function VacationStatusBadge({ status }: { status: KamVacation["status"] }) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Plane size={11} />
        Actief
      </span>
    );
  }
  if (status === "scheduled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Gepland
      </span>
    );
  }
  if (status === "finished") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <PlaneLanding size={11} />
        Afgerond
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
      Geannuleerd
    </span>
  );
}

// ── KAM Vacation Modal ───────────────────────────────────────────────────

interface VacationModalProps {
  kams: KAMUser[];
  onClose: () => void;
  onSaved: () => void;
}

function VacationModal({ kams, onClose, onSaved }: VacationModalProps) {
  const [kamId, setKamId] = useState<number | "">("");
  const [substituteKamId, setSubstituteKamId] = useState<number | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!kamId || !substituteKamId || !startDate || !endDate) {
      setError("Alle velden zijn verplicht.");
      return;
    }
    if (kamId === substituteKamId) {
      setError("KAM en vervanger mogen niet dezelfde persoon zijn.");
      return;
    }
    if (startDate > endDate) {
      setError("Startdatum mag niet na einddatum liggen.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await post("/admin/mail/vacations", {
        kam_id: kamId,
        substitute_kam_id: substituteKamId,
        start_date: startDate,
        end_date: endDate,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            <Plane size={18} style={{ color: "#65ADDD" }} />
            Nieuwe Vakantie
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              KAM <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={kamId}
                onChange={(e) => setKamId(e.target.value === "" ? "" : Number(e.target.value))}
                className="input-field appearance-none pr-10"
              >
                <option value="">— Selecteer KAM —</option>
                {kams.map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Vervanger <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={substituteKamId}
                onChange={(e) => setSubstituteKamId(e.target.value === "" ? "" : Number(e.target.value))}
                className="input-field appearance-none pr-10"
              >
                <option value="">— Selecteer vervanger —</option>
                {kams.filter((k) => k.id !== kamId).map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Startdatum <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Einddatum <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field"
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary min-h-[44px] sm:min-h-0">
              Annuleren
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
            >
              {saving ? (
                <><Loader2 size={14} className="animate-spin" />Opslaan…</>
              ) : (
                "Vakantie Aanmaken"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KAMsTab({ kams, hmps, onRefresh }: KAMsTabProps) {
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<KAMUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<KAMUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Vacation state
  const [vacations, setVacations] = useState<KamVacation[]>([]);
  const [vacationsLoading, setVacationsLoading] = useState(true);
  const [showVacationModal, setShowVacationModal] = useState(false);

  const fetchVacations = useCallback(() => {
    setVacationsLoading(true);
    get<KamVacation[]>("/admin/mail/vacations")
      .then(setVacations)
      .catch(() => setVacations([]))
      .finally(() => setVacationsLoading(false));
  }, []);

  useEffect(() => {
    fetchVacations();
  }, [fetchVacations]);

  async function handleDeleteVacation(id: number) {
    if (!confirm("Vakantie annuleren? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await del(`/admin/mail/vacations/${id}`);
      fetchVacations();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Annuleren mislukt.");
    }
  }

  function hmpCountForKam(kamId: number): number {
    return hmps.filter((h) => h.kam_id === kamId).length;
  }

  async function handleDelete() {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await del(`/users/${deleteUser.id}`);
      onRefresh();
      setDeleteUser(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Verwijderen mislukt.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          Beheer alle Key Account Managers in het portaal.
        </p>
        <button
          onClick={() => {
            setEditUser(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
        >
          <UserPlus size={16} />
          KAM Toevoegen
        </button>
      </div>

      <div className="card overflow-hidden">
        {kams.length === 0 ? (
          <EmptyState message="Geen KAMs gevonden. Voeg een KAM toe om te beginnen." icon={Users} />
        ) : (
          <>
            {/* Mobile card view */}
            <div className="block md:hidden divide-y divide-slate-100">
              {kams.map((kam) => (
                <div key={kam.id} className="p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: "#65ADDD" }}
                    >
                      {kam.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{kam.name}</div>
                      <div className="text-xs text-slate-500 truncate">{kam.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue">
                      {hmpCountForKam(kam.id)} HMP{hmpCountForKam(kam.id) !== 1 ? "s" : ""}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditUser(kam);
                          setShowModal(true);
                        }}
                        className="p-2 rounded hover:bg-waves-blue-light text-slate-400 hover:text-waves-blue transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title="Bewerken"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteUser(kam)}
                        className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title="Verwijderen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <table className="w-full text-sm hidden md:table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Naam
                  </th>
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    E-mail
                  </th>
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Aantal HMPs
                  </th>
                  <th className="text-right py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody>
                {kams.map((kam) => (
                  <tr
                    key={kam.id}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50"
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: "#65ADDD" }}
                        >
                          {kam.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800">{kam.name}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-5 text-slate-500">{kam.email}</td>
                    <td className="py-3.5 px-5">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue">
                        {hmpCountForKam(kam.id)} HMP{hmpCountForKam(kam.id) !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="py-3.5 px-5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditUser(kam);
                            setShowModal(true);
                          }}
                          className="p-1.5 rounded hover:bg-waves-blue-light text-slate-400 hover:text-waves-blue transition-colors"
                          title="Bewerken"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteUser(kam)}
                          className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          title="Verwijderen"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {showModal && (
        <KAMModal
          initial={editUser}
          onClose={() => {
            setShowModal(false);
            setEditUser(null);
          }}
          onSaved={onRefresh}
        />
      )}

      {deleteUser && (
        <ConfirmDialog
          name={deleteUser.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteUser(null)}
          saving={deleting}
        />
      )}

      {/* Vakanties sectie */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Plane size={15} />
            Vakanties
          </h3>
          <button
            onClick={() => setShowVacationModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-waves-blue text-white hover:bg-waves-blue-hover transition-colors min-h-[44px] sm:min-h-0"
          >
            <Plus size={14} />
            Nieuwe vakantie
          </button>
        </div>

        <div className="card overflow-hidden">
          {vacationsLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-slate-400">
              <Loader2 size={14} className="animate-spin" />
              Laden…
            </div>
          ) : vacations.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              Geen vakanties gevonden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">KAM</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Vervanger</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Periode</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="text-right py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {vacations.map((v) => (
                    <tr key={v.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{v.kam_name}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 hidden sm:table-cell">{v.substitute_kam_name}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                        {new Date(v.start_date).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" })}
                        {" – "}
                        {new Date(v.end_date).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="py-3 px-4">
                        <VacationStatusBadge status={v.status} />
                      </td>
                      <td className="py-3 px-4 text-right">
                        {(v.status === "scheduled" || v.status === "active") && (
                          <button
                            onClick={() => handleDeleteVacation(v.id)}
                            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ml-auto"
                            title="Vakantie annuleren"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showVacationModal && (
        <VacationModal
          kams={kams}
          onClose={() => setShowVacationModal(false)}
          onSaved={fetchVacations}
        />
      )}
    </>
  );
}

// ── Relative time helper ─────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "Nooit";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} dgn geleden`;
  if (hours > 0) return `${hours} uur geleden`;
  if (mins > 0) return `${mins} min geleden`;
  return "Zojuist";
}

// ── HMPs Tab ─────────────────────────────────────────────────────────────────

interface HMPsTabProps {
  hmps: HMPUser[];
  kams: KAMUser[];
  partners: Partner[];
  allTabs: Tab[];
  onRefresh: () => void;
}

function HMPsTab({ hmps, kams, partners, allTabs, onRefresh }: HMPsTabProps) {
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<HMPUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<HMPUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Mail state
  const [mailConfirmUser, setMailConfirmUser] = useState<HMPUser | null>(null);
  const [mailSending, setMailSending] = useState(false);
  const [onboardingConfirmUser, setOnboardingConfirmUser] = useState<HMPUser | null>(null);
  const [onboardingSending, setOnboardingSending] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // KAM-mail force-send state
  const [forceLoading, setForceLoading] = useState<{ id: number; type: string } | null>(null);

  async function handleForceKamMail(
    hmp: HMPUser,
    type: "bundle" | "inactivity" | "no-changes"
  ) {
    const labels: Record<string, string> = {
      bundle: "sessie-mail",
      inactivity: "inactiviteits-warning",
      "no-changes": "geen-wijzigingen-mail",
    };
    if (!confirm(`Weet je zeker dat je de ${labels[type]} voor ${hmp.name} wilt versturen?`)) return;
    const endpoints: Record<string, string> = {
      bundle: `/admin/mail/kam-bundle/force/${hmp.id}`,
      inactivity: `/admin/mail/inactivity/force-warning/${hmp.id}`,
      "no-changes": `/admin/mail/no-changes/force/${hmp.id}`,
    };
    setForceLoading({ id: hmp.id, type });
    try {
      await post(endpoints[type], {});
      setToast({ message: `${labels[type].charAt(0).toUpperCase() + labels[type].slice(1)} verzonden voor ${hmp.name}`, type: "success" });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : "Versturen mislukt.",
        type: "error",
      });
    } finally {
      setForceLoading(null);
    }
  }

  async function handleSendMail() {
    if (!mailConfirmUser) return;
    setMailSending(true);
    try {
      await post(`/admin/mail/send/${mailConfirmUser.id}`, {});
      setToast({ message: `E-mail verzonden naar ${mailConfirmUser.email}`, type: "success" });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : "Verzenden mislukt.",
        type: "error",
      });
    } finally {
      setMailSending(false);
      setMailConfirmUser(null);
    }
  }

  async function handleSendOnboarding() {
    if (!onboardingConfirmUser) return;
    setOnboardingSending(true);
    try {
      await post(`/admin/mail/onboarding/${onboardingConfirmUser.id}`, {});
      setToast({
        message: `Welkomstmail verzonden naar ${onboardingConfirmUser.email}`,
        type: "success",
      });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : "Verzenden mislukt.",
        type: "error",
      });
    } finally {
      setOnboardingSending(false);
      setOnboardingConfirmUser(null);
    }
  }

  function partnerName(id: number | null): string {
    if (!id) return "—";
    return partners.find((p) => p.id === id)?.name ?? "—";
  }

  function kamName(id: number | null): string {
    if (!id) return "—";
    return kams.find((k) => k.id === id)?.name ?? "—";
  }

  function vestigingNames(tabIds: number[]): string {
    if (!tabIds.length) return "—";
    const names = tabIds
      .map((id) => allTabs.find((t) => t.id === id)?.name)
      .filter(Boolean);
    if (!names.length) return "—";
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }

  async function handleDelete() {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await del(`/users/${deleteUser.id}`);
      onRefresh();
      setDeleteUser(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Verwijderen mislukt.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          Beheer alle Hiring Manager Portaal gebruikers.
        </p>
        <button
          onClick={() => {
            setEditUser(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
        >
          <UserPlus size={16} />
          HMP Toevoegen
        </button>
      </div>

      <div className="card overflow-hidden">
        {hmps.length === 0 ? (
          <EmptyState message="Geen HMPs gevonden. Voeg een HMP toe om te beginnen." icon={Users} />
        ) : (
          <>
            {/* Mobile card view */}
            <div className="block md:hidden divide-y divide-slate-100">
              {hmps.map((hmp) => (
                <div key={hmp.id} className="p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: "#7C3AED" }}
                    >
                      {hmp.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{hmp.name}</div>
                      <div className="text-xs text-slate-500 truncate">{hmp.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <HMPStatusBadge lastLogin={hmp.last_login ?? null} />
                    {hmp.partner_id && (
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">
                        {partnerName(hmp.partner_id)}
                      </span>
                    )}
                    {hmp.kam_id && (
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">
                        KAM: {kamName(hmp.kam_id)}
                      </span>
                    )}
                    {hmp.tab_permissions.length > 0 && (
                      <span className="text-xs bg-waves-blue-light px-2 py-0.5 rounded text-waves-blue">
                        {vestigingNames(hmp.tab_permissions)}
                      </span>
                    )}
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-500">
                      {hmp.review_interval_days != null
                        ? `${hmp.review_interval_days} dgn`
                        : "30 dgn (std.)"}
                    </span>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1 pt-1">
                    <button
                      onClick={() => setOnboardingConfirmUser(hmp)}
                      className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium hover:bg-waves-blue-light transition-colors min-h-[44px]"
                      style={{ color: "#65ADDD" }}
                      title={hmp.last_login ? "Stuur welkomstmail opnieuw" : "Stuur welkomstmail"}
                    >
                      <Sparkles size={13} />
                      Welkomstmail
                    </button>
                    <button
                      onClick={() => setMailConfirmUser(hmp)}
                      className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium hover:bg-waves-blue-light transition-colors min-h-[44px]"
                      style={{ color: "#65ADDD" }}
                      title="Stuur review-mail"
                    >
                      <Mail size={13} />
                      Review-mail
                    </button>
                    <button
                      onClick={() => window.open(`/api/admin/mail/preview/${hmp.id}`, "_blank")}
                      className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors min-h-[44px]"
                      title="Voorbeeld review-mail"
                    >
                      <Eye size={13} />
                      Voorbeeld
                    </button>
                    <button
                      onClick={() => {
                        setEditUser(hmp);
                        setShowModal(true);
                      }}
                      className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium text-waves-blue hover:bg-waves-blue-light transition-colors min-h-[44px]"
                    >
                      <Pencil size={13} />
                      Bewerken
                    </button>
                    <button
                      onClick={() => setDeleteUser(hmp)}
                      className="flex items-center gap-1 px-3 py-2 rounded text-xs font-medium text-red-500 hover:bg-red-50 transition-colors min-h-[44px]"
                    >
                      <Trash2 size={13} />
                      Verwijderen
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Naam
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      E-mail
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Partner
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Vestigingen
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      KAM
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      Termijn
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      Laatste activiteit
                    </th>
                    <th className="text-right py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hmps.map((hmp) => (
                    <tr
                      key={hmp.id}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50"
                    >
                      <td className="py-3.5 px-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: "#7C3AED" }}
                          >
                            {hmp.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800 whitespace-nowrap">{hmp.name}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-3 text-slate-500 whitespace-nowrap">{hmp.email}</td>
                      <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                        {partnerName(hmp.partner_id)}
                      </td>
                      <td className="py-3.5 px-3 text-slate-500 max-w-[180px]">
                        <span className="truncate block" title={
                          hmp.tab_permissions.length
                            ? hmp.tab_permissions.map((id) => allTabs.find((t) => t.id === id)?.name).filter(Boolean).join(", ")
                            : "—"
                        }>
                          {vestigingNames(hmp.tab_permissions)}
                        </span>
                      </td>
                      <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                        {kamName(hmp.kam_id)}
                      </td>
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        {hmp.review_interval_days != null ? (
                          <span className="text-slate-600">{hmp.review_interval_days} dgn</span>
                        ) : (
                          <span className="text-slate-400" title="Standaard">30 dgn</span>
                        )}
                      </td>
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        <HMPStatusBadge lastLogin={hmp.last_login ?? null} />
                      </td>
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        <span className="text-xs text-slate-500">{relativeTime(hmp.last_activity_at)}</span>
                      </td>
                      <td className="py-3.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            onClick={() => setOnboardingConfirmUser(hmp)}
                            className="p-2 rounded hover:bg-waves-blue-light text-slate-400 transition-colors"
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#65ADDD")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                            title={hmp.last_login ? "Stuur welkomstmail opnieuw" : "Stuur welkomstmail"}
                          >
                            <Sparkles size={15} />
                          </button>
                          <button
                            onClick={() => setMailConfirmUser(hmp)}
                            className="p-2 rounded hover:bg-waves-blue-light text-slate-400 transition-colors"
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#65ADDD")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                            title="Stuur review-mail"
                          >
                            <Mail size={15} />
                          </button>
                          <button
                            onClick={() => window.open(`/api/admin/mail/preview/${hmp.id}`, "_blank")}
                            className="p-2 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Voorbeeld review-mail"
                          >
                            <Eye size={15} />
                          </button>
                          {/* Force sessie-mail + voorbeeld */}
                          <div className="flex flex-col items-center gap-0.5">
                            <button
                              onClick={() => handleForceKamMail(hmp, "bundle")}
                              disabled={forceLoading?.id === hmp.id}
                              className="p-1.5 rounded hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors"
                              title="Force sessie-mail"
                            >
                              {forceLoading?.id === hmp.id && forceLoading.type === "bundle" ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Send size={14} />
                              )}
                            </button>
                            <button
                              onClick={() => window.open(`/api/admin/mail/kam-bundle/preview/${hmp.id}`, "_blank")}
                              className="text-[10px] text-waves-blue hover:underline leading-none"
                              title="Voorbeeld sessie-mail"
                            >
                              Voorbeeld
                            </button>
                          </div>
                          {/* Force inactiviteits-warning + voorbeeld */}
                          <div className="flex flex-col items-center gap-0.5">
                            <button
                              onClick={() => handleForceKamMail(hmp, "inactivity")}
                              disabled={forceLoading?.id === hmp.id}
                              className="p-1.5 rounded hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors"
                              title="Force inactiviteits-warning"
                            >
                              {forceLoading?.id === hmp.id && forceLoading.type === "inactivity" ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <AlertTriangle size={14} />
                              )}
                            </button>
                            <button
                              onClick={() => window.open(`/api/admin/mail/inactivity/preview/${hmp.id}?type=warning`, "_blank")}
                              className="text-[10px] text-waves-blue hover:underline leading-none"
                              title="Voorbeeld inactiviteits-warning"
                            >
                              Voorbeeld
                            </button>
                          </div>
                          {/* Force geen-wijzigingen-mail + voorbeeld */}
                          <div className="flex flex-col items-center gap-0.5">
                            <button
                              onClick={() => handleForceKamMail(hmp, "no-changes")}
                              disabled={forceLoading?.id === hmp.id}
                              className="p-1.5 rounded hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors"
                              title="Force geen-wijzigingen-mail"
                            >
                              {forceLoading?.id === hmp.id && forceLoading.type === "no-changes" ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <CalendarOff size={14} />
                              )}
                            </button>
                            <button
                              onClick={() => window.open(`/api/admin/mail/no-changes/preview/${hmp.id}`, "_blank")}
                              className="text-[10px] text-waves-blue hover:underline leading-none"
                              title="Voorbeeld geen-wijzigingen-mail"
                            >
                              Voorbeeld
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              setEditUser(hmp);
                              setShowModal(true);
                            }}
                            className="p-2 rounded hover:bg-waves-blue-light text-slate-400 hover:text-waves-blue transition-colors"
                            title="Bewerken"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => setDeleteUser(hmp)}
                            className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            title="Verwijderen"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <HMPModal
          initial={editUser}
          partners={partners}
          kams={kams}
          onClose={() => {
            setShowModal(false);
            setEditUser(null);
          }}
          onSaved={onRefresh}
        />
      )}

      {deleteUser && (
        <ConfirmDialog
          name={deleteUser.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteUser(null)}
          saving={deleting}
        />
      )}

      {mailConfirmUser && (
        <MailConfirmDialog
          hmp={mailConfirmUser}
          onConfirm={handleSendMail}
          onCancel={() => setMailConfirmUser(null)}
          sending={mailSending}
        />
      )}

      {onboardingConfirmUser && (
        <OnboardingConfirmDialog
          hmp={onboardingConfirmUser}
          isResend={!!onboardingConfirmUser.last_login}
          onConfirm={handleSendOnboarding}
          onCancel={() => setOnboardingConfirmUser(null)}
          sending={onboardingSending}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

// ── Admins Tab ────────────────────────────────────────────────────────────────

interface EmailLogEntry {
  id: number;
  recipient_email: string;
  recipient_name?: string;
  type: string;
  status: "sent" | "failed" | "preview";
  error_message?: string | null;
  created_at: string;
}

function EmailLogStatusBadge({ status }: { status: string }) {
  if (status === "sent") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Verzonden
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Mislukt
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      Voorbeeld
    </span>
  );
}

function AdminsTab({ admins }: { admins: AdminUser[] }) {
  const [emailLog, setEmailLog] = useState<EmailLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState("");

  useEffect(() => {
    get<EmailLogEntry[]>("/admin/mail/log")
      .then((entries) => setEmailLog(entries.slice(0, 50)))
      .catch((err) => setLogError(err instanceof Error ? err.message : "Laden mislukt."))
      .finally(() => setLogLoading(false));
  }, []);

  return (
    <>
      <div className="mb-4">
        <p className="text-sm text-slate-500">
          Overzicht van alle beheerders in het portaal (alleen lezen).
        </p>
      </div>
      <div className="card overflow-hidden">
        {admins.length === 0 ? (
          <EmptyState message="Geen beheerders gevonden." icon={Users} />
        ) : (
          <>
            {/* Mobile card view */}
            <div className="block md:hidden divide-y divide-slate-100">
              {admins.map((admin) => (
                <div key={admin.id} className="p-4 flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: "#1B2A4A" }}
                  >
                    {admin.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{admin.name}</div>
                    <div className="text-xs text-slate-500 truncate">{admin.email}</div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue flex-shrink-0">
                    Beheerder
                  </span>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <table className="w-full text-sm hidden md:table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Naam
                  </th>
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    E-mail
                  </th>
                  <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Rol
                  </th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50"
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: "#1B2A4A" }}
                        >
                          {admin.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800">{admin.name}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-5 text-slate-500">{admin.email}</td>
                    <td className="py-3.5 px-5">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue">
                        Beheerder
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* E-mail historie */}
      <div className="mt-8">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Mail size={15} />
          E-mail historie
        </h3>
        <div className="card overflow-hidden">
          {logLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-slate-400">
              <Loader2 size={14} className="animate-spin" />
              Laden…
            </div>
          ) : logError ? (
            <div className="p-4 text-sm text-red-600 bg-red-50">
              Fout bij laden: {logError}
            </div>
          ) : emailLog.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              Geen e-mail logs gevonden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Datum</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Ontvanger</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Type</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Foutbericht</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLog.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleString("nl-NL", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-700 truncate max-w-[160px]">
                          {entry.recipient_name ?? entry.recipient_email}
                        </div>
                        {entry.recipient_name && (
                          <div className="text-xs text-slate-400 truncate">{entry.recipient_email}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500 hidden sm:table-cell">
                        <span className="capitalize">{entry.type.replace(/_/g, " ")}</span>
                      </td>
                      <td className="py-3 px-4">
                        <EmailLogStatusBadge status={entry.status} />
                      </td>
                      <td className="py-3 px-4 text-xs text-red-600 max-w-[200px] truncate hidden md:table-cell">
                        {entry.error_message ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Gebruikersbeheer page ────────────────────────────────────────────────

export default function Gebruikersbeheer() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("kams");
  const [users, setUsers] = useState<UserPublic[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [allTabs, setAllTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      get<UserPublic[]>("/users"),
      get<Partner[]>("/partners"),
      get<Tab[]>("/tabs"),
    ])
      .then(([u, p, t]) => {
        setUsers(u);
        setPartners(p);
        setAllTabs(t);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Laden mislukt."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kams = users.filter((u): u is KAMUser => u.role === "kam");
  const hmps = users.filter((u): u is HMPUser => u.role === "hmp");
  const admins = users.filter((u): u is AdminUser => u.role === "admin");

  const tabs: { key: ActiveTab; label: string; count: number }[] = [
    { key: "kams", label: "KAMs", count: kams.length },
    { key: "hmps", label: "HMPs", count: hmps.length },
    { key: "admins", label: "Admins", count: admins.length },
  ];

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      {/* Tab bar — full width on mobile, fit-content on desktop */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto w-full md:w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap flex-1 md:flex-none justify-center min-h-[44px] md:min-h-0 ${
              activeTab === tab.key
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
            <span
              className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
                activeTab === tab.key
                  ? "bg-waves-blue text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "kams" && (
        <KAMsTab kams={kams} hmps={hmps} onRefresh={fetchData} />
      )}
      {activeTab === "hmps" && (
        <HMPsTab
          hmps={hmps}
          kams={kams}
          partners={partners}
          allTabs={allTabs}
          onRefresh={fetchData}
        />
      )}
      {activeTab === "admins" && <AdminsTab admins={admins} />}
    </div>
  );
}
